// Компонент мониторинга питания ММ27 и ММ28 - с детальной информацией по ОУ
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { saveAs } from "file-saver-es";
import * as XLSX from 'xlsx';

const API_MM27 = 'https://apimm.cemon.ru/api/data';
const API_MM28 = 'https://apimm.cemon.ru/api/compliance-data';
const API_SCHOOL_DETAILS = 'https://api.cemon.ru/otcheti/mmproblem/pisheblok/';

const monthNames = {
    1: 'Янв', 2: 'Фев', 3: 'Мар', 4: 'Апр',
    5: 'Май', 6: 'Июн', 7: 'Июл', 8: 'Авг',
    9: 'Сен', 10: 'Окт', 11: 'Ноя', 12: 'Дек'
};

const fullMonthNames = {
    1: 'Январь', 2: 'Февраль', 3: 'Март', 4: 'Апрель',
    5: 'Май', 6: 'Июнь', 7: 'Июль', 8: 'Август',
    9: 'Сентябрь', 10: 'Октябрь', 11: 'Ноябрь', 12: 'Декабрь'
};

const quarterMonths = {
    1: [1, 2, 3],
    2: [4, 5, 6],
    3: [7, 8, 9],
    4: [10, 11, 12]
};

const MonitoringMM = () => {
    const [regionsData, setRegionsData] = useState([]);
    const [districtsData, setDistrictsData] = useState(new Map());
    const [monthsList, setMonthsList] = useState([]);
    const [filteredMonthsList, setFilteredMonthsList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingStatus, setLoadingStatus] = useState('Инициализация...');
    const [currentFilter, setCurrentFilter] = useState({ quarter: 'all', region: '', district: '' });
    const [modalOpen, setModalOpen] = useState(false);
    const [currentSchools, setCurrentSchools] = useState([]);
    const [currentDistrictName, setCurrentDistrictName] = useState('');
    const [expandedRegions, setExpandedRegions] = useState({});
    const [stats, setStats] = useState({ avgMM27: 0, avgMM28: 0, mm27Count: 0, mm28Count: 0 });
    const [methodModalOpen, setMethodModalOpen] = useState(false);
    const [allData, setAllData] = useState({});
    
    // Состояния для модального окна с деталями ОУ
    const [schoolModalOpen, setSchoolModalOpen] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [schoolDetails, setSchoolDetails] = useState(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [detailsMonth, setDetailsMonth] = useState(null);
    
    const modalBodyRef = useRef(null);
    const methodModalBodyRef = useRef(null);
    const abortControllerRef = useRef(null);
    const isMountedRef = useRef(true);

    const currentYear = 2026;
    const currentMonthNum = new Date().getMonth() + 1;

    useEffect(() => {
        isMountedRef.current = true;
        const months = [];
        for (let m = 1; m <= currentMonthNum; m++) months.push(m);
        setMonthsList(months);
        setFilteredMonthsList(months);
        
        loadAllData(months);
        
        return () => {
            isMountedRef.current = false;
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const loadAllData = async (months) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        abortControllerRef.current = new AbortController();
        
        setIsLoading(true);
        setLoadingProgress(0);
        setLoadingStatus('Загрузка данных...');
        
        const rawData = {};
        let completedCount = 0;
        const totalMonths = months.length;
        
        const concurrencyLimit = 6;
        const queue = [...months];
        
        const loadMonth = async (month) => {
            try {
                if (!isMountedRef.current) return false;
                
                const [responseMM27, responseMM28] = await Promise.all([
                    fetch(`${API_MM27}?m=${month}&g=${currentYear}`, {
                        signal: abortControllerRef.current.signal
                    }),
                    fetch(`${API_MM28}?m=${month}&g=${currentYear}`, {
                        signal: abortControllerRef.current.signal
                    })
                ]);

                if (!responseMM27.ok || !responseMM28.ok) {
                    throw new Error(`Ошибка загрузки данных за ${monthNames[month]}`);
                }

                const [mm27Data, mm28Data] = await Promise.all([
                    responseMM27.json(),
                    responseMM28.json()
                ]);

                rawData[month] = { mm27: mm27Data, mm28: mm28Data };
                
                completedCount++;
                const progress = Math.round((completedCount / totalMonths) * 100);
                if (isMountedRef.current) {
                    setLoadingProgress(progress);
                    setLoadingStatus(`Загружено ${completedCount} из ${totalMonths} месяцев (${progress}%)`);
                }
                
                return true;
            } catch (error) {
                if (error.name === 'AbortError') return false;
                console.error(`Ошибка загрузки за ${monthNames[month]}:`, error);
                rawData[month] = { mm27: [], mm28: [], error: true };
                completedCount++;
                if (isMountedRef.current) {
                    setLoadingProgress(Math.round((completedCount / totalMonths) * 100));
                }
                return false;
            }
        };
        
        const runWithConcurrency = async () => {
            const workers = [];
            for (let i = 0; i < concurrencyLimit; i++) {
                workers.push((async () => {
                    while (queue.length > 0 && !abortControllerRef.current?.signal.aborted && isMountedRef.current) {
                        const month = queue.shift();
                        if (month) await loadMonth(month);
                    }
                })());
            }
            await Promise.all(workers);
        };
        
        await runWithConcurrency();
        
        if (abortControllerRef.current?.signal.aborted || !isMountedRef.current) return;
        
        setLoadingStatus('Обработка данных...');
        setLoadingProgress(95);
        
        setAllData(rawData);
        
        const result = processRawData(rawData, months);
        
        if (result && isMountedRef.current) {
            setRegionsData(result.regionsList);
            setDistrictsData(result.districtsByRegion);
            
            const newStats = calculateStats(result.regionsList, filteredMonthsList, currentFilter, result.districtsByRegion);
            setStats(newStats);
        }
        
        setLoadingProgress(100);
        setLoadingStatus('Обработка данных...');
        
        setIsLoading(false);
    };

    const processRawData = (rawData, months) => {
        const regionsMap = new Map();
        const districtsMap = new Map();
        
        for (const month of months) {
            const monthData = rawData[month];
            if (!monthData || monthData.error) continue;
            
            if (monthData.mm27 && monthData.mm27.length > 0) {
                for (const item of monthData.mm27) {
                    const region = item.Регион || item.РегионНаименование;
                    if (!region) continue;
                    
                    if (!regionsMap.has(region)) {
                        regionsMap.set(region, { 
                            name: region, 
                            districts: new Set(),
                            mm27Total: {},
                            mm27Compliant: {},
                            mm28Total: {},
                            mm28Compliant: {}
                        });
                    }
                    const regionObj = regionsMap.get(region);
                    
                    regionObj.mm27Total[month] = (regionObj.mm27Total[month] || 0) + 1;
                    if (item.СоблюдениеЗаПериод === 1) {
                        regionObj.mm27Compliant[month] = (regionObj.mm27Compliant[month] || 0) + 1;
                    }
                    
                    const district = item.Район;
                    if (district && district !== 'NULL' && district !== '') {
                        regionObj.districts.add(district);
                        const districtKey = `${region}|${district}`;
                        if (!districtsMap.has(districtKey)) {
                            districtsMap.set(districtKey, {
                                region,
                                name: district,
                                mm27Total: {},
                                mm27Compliant: {},
                                mm28Total: {},
                                mm28Compliant: {}
                            });
                        }
                        const districtObj = districtsMap.get(districtKey);
                        districtObj.mm27Total[month] = (districtObj.mm27Total[month] || 0) + 1;
                        if (item.СоблюдениеЗаПериод === 1) {
                            districtObj.mm27Compliant[month] = (districtObj.mm27Compliant[month] || 0) + 1;
                        }
                    }
                }
            }
            
            if (monthData.mm28 && monthData.mm28.length > 0) {
                for (const item of monthData.mm28) {
                    const region = item.РегионНаименование || item.Регион;
                    if (!region) continue;
                    
                    let regionObj = regionsMap.get(region);
                    if (!regionObj) {
                        regionObj = { 
                            name: region, 
                            districts: new Set(),
                            mm27Total: {},
                            mm27Compliant: {},
                            mm28Total: {},
                            mm28Compliant: {}
                        };
                        regionsMap.set(region, regionObj);
                    }
                    
                    regionObj.mm28Total[month] = (regionObj.mm28Total[month] || 0) + 1;
                    if (item.СоблюдениеЗаПериод === 1) {
                        regionObj.mm28Compliant[month] = (regionObj.mm28Compliant[month] || 0) + 1;
                    }
                    
                    const district = item.Район;
                    if (district && district !== 'NULL' && district !== '') {
                        regionObj.districts.add(district);
                        const districtKey = `${region}|${district}`;
                        let districtObj = districtsMap.get(districtKey);
                        if (!districtObj) {
                            districtObj = {
                                region,
                                name: district,
                                mm27Total: {},
                                mm27Compliant: {},
                                mm28Total: {},
                                mm28Compliant: {}
                            };
                            districtsMap.set(districtKey, districtObj);
                        }
                        districtObj.mm28Total[month] = (districtObj.mm28Total[month] || 0) + 1;
                        if (item.СоблюдениеЗаПериод === 1) {
                            districtObj.mm28Compliant[month] = (districtObj.mm28Compliant[month] || 0) + 1;
                        }
                    }
                }
            }
        }
        
        const regionsList = [];
        for (const [name, region] of regionsMap) {
            const monthsData = {};
            for (const month of months) {
                const mm27Total = region.mm27Total[month] || 0;
                const mm27Compliant = region.mm27Compliant[month] || 0;
                const mm28Total = region.mm28Total[month] || 0;
                const mm28Compliant = region.mm28Compliant[month] || 0;
                
                const mm27Percent = mm27Total > 0 ? Math.round((mm27Compliant / mm27Total) * 100) : 0;
                const mm28Percent = mm28Total > 0 ? Math.round((mm28Compliant / mm28Total) * 100) : 0;
                monthsData[month] = { mm27Percent, mm28Percent };
            }
            regionsList.push({
                name: name,
                monthsData: monthsData,
                districts: Array.from(region.districts).sort()
            });
        }
        
        const districtsByRegion = new Map();
        for (const [, district] of districtsMap) {
            const monthsData = {};
            for (const month of months) {
                const mm27Total = district.mm27Total[month] || 0;
                const mm27Compliant = district.mm27Compliant[month] || 0;
                const mm28Total = district.mm28Total[month] || 0;
                const mm28Compliant = district.mm28Compliant[month] || 0;
                
                const mm27Percent = mm27Total > 0 ? Math.round((mm27Compliant / mm27Total) * 100) : 0;
                const mm28Percent = mm28Total > 0 ? Math.round((mm28Compliant / mm28Total) * 100) : 0;
                monthsData[month] = { mm27Percent, mm28Percent };
            }
            
            if (!districtsByRegion.has(district.region)) {
                districtsByRegion.set(district.region, []);
            }
            districtsByRegion.get(district.region).push({
                name: district.name,
                monthsData: monthsData,
                region: district.region
            });
        }
        
        regionsList.sort((a, b) => a.name.localeCompare(b.name));
        for (const [, districts] of districtsByRegion) {
            districts.sort((a, b) => a.name.localeCompare(b.name));
        }
        
        return { regionsList, districtsByRegion };
    };

    const calculateStats = useCallback((regions, months, filter, districtsMapData) => {
        if (!regions || regions.length === 0) {
            return { avgMM27: 0, avgMM28: 0, mm27Count: 0, mm28Count: 0 };
        }
        
        if (filter.district && filter.district !== '' && filter.region) {
            const districts = districtsMapData.get(filter.region) || [];
            const selectedDistrict = districts.find(d => d.name === filter.district);
            
            if (selectedDistrict) {
                let mm27Sum = 0;
                let mm28Sum = 0;
                let mm27Count = 0;
                let mm28Count = 0;
                
                for (const month of months) {
                    const data = selectedDistrict.monthsData[month];
                    if (data) {
                        mm27Sum += data.mm27Percent;
                        mm27Count++;
                        mm28Sum += data.mm28Percent;
                        mm28Count++;
                    }
                }
                
                return {
                    avgMM27: mm27Count > 0 ? Math.round(mm27Sum / mm27Count) : 0,
                    avgMM28: mm28Count > 0 ? Math.round(mm28Sum / mm28Count) : 0,
                    mm27Count,
                    mm28Count
                };
            }
        }
        
        let filteredRegions = [...regions];
        
        if (filter.region) {
            filteredRegions = filteredRegions.filter(r => r.name === filter.region);
        }
        
        let totalMM27Sum = 0;
        let totalMM28Sum = 0;
        let mm27Count = 0;
        let mm28Count = 0;
        
        for (const month of months) {
            let monthMM27Sum = 0;
            let monthMM28Sum = 0;
            let monthMM27Regions = 0;
            let monthMM28Regions = 0;
            
            for (const region of filteredRegions) {
                const monthData = region.monthsData[month];
                if (monthData) {
                    monthMM27Sum += monthData.mm27Percent;
                    monthMM27Regions++;
                    monthMM28Sum += monthData.mm28Percent;
                    monthMM28Regions++;
                }
            }
            
            if (monthMM27Regions > 0) {
                totalMM27Sum += monthMM27Sum / monthMM27Regions;
                mm27Count++;
            }
            if (monthMM28Regions > 0) {
                totalMM28Sum += monthMM28Sum / monthMM28Regions;
                mm28Count++;
            }
        }
        
        return {
            avgMM27: mm27Count > 0 ? Math.round(totalMM27Sum / mm27Count) : 0,
            avgMM28: mm28Count > 0 ? Math.round(totalMM28Sum / mm28Count) : 0,
            mm27Count,
            mm28Count
        };
    }, []);

    useEffect(() => {
        if (regionsData.length > 0 && districtsData.size > 0) {
            const newStats = calculateStats(regionsData, filteredMonthsList, currentFilter, districtsData);
            setStats(newStats);
        }
    }, [currentFilter.region, currentFilter.district, filteredMonthsList, regionsData, districtsData, calculateStats]);

    const forceRefresh = () => {
        setIsLoading(true);
        const months = [];
        for (let m = 1; m <= currentMonthNum; m++) months.push(m);
        loadAllData(months);
    };

    const getDisplayData = useCallback(() => {
        if (currentFilter.district && currentFilter.district !== '' && currentFilter.region) {
            const districts = districtsData.get(currentFilter.region) || [];
            const selectedDistrict = districts.find(d => d.name === currentFilter.district);
            
            if (selectedDistrict) {
                return {
                    type: 'district',
                    data: selectedDistrict,
                    regionName: currentFilter.region
                };
            }
        }
        
        let regions = [...regionsData];
        if (currentFilter.region) {
            regions = regions.filter(r => r.name === currentFilter.region);
        }
        
        return {
            type: 'regions',
            data: regions
        };
    }, [regionsData, districtsData, currentFilter.region, currentFilter.district]);

    const getDistrictsForRegion = useCallback((regionName) => {
        return districtsData.get(regionName) || [];
    }, [districtsData]);

    const getSchoolsInDistrict = useCallback(async (regionName, districtName) => {
        const schoolsMap = new Map();
        
        for (const month of filteredMonthsList) {
            const monthData = allData[month];
            if (!monthData || monthData.error) continue;
            
            for (const item of monthData.mm27) {
                const region = item.Регион || item.РегионНаименование;
                if (region === regionName && item.Район === districtName) {
                    const schoolName = item.Пищеблок || item.Наименование;
                    if (!schoolsMap.has(schoolName)) {
                        schoolsMap.set(schoolName, { name: schoolName, data: {}, region: regionName, district: districtName });
                    }
                    const school = schoolsMap.get(schoolName);
                    if (!school.data[month]) school.data[month] = {};
                    school.data[month].mm27 = item.СоблюдениеЗаПериод;
                }
            }
            
            for (const item of monthData.mm28) {
                const region = item.РегионНаименование || item.Регион;
                if (region === regionName && item.Район === districtName) {
                    const schoolName = item.Пищеблок || item.Наименование;
                    if (!schoolsMap.has(schoolName)) {
                        schoolsMap.set(schoolName, { name: schoolName, data: {}, region: regionName, district: districtName });
                    }
                    const school = schoolsMap.get(schoolName);
                    if (!school.data[month]) school.data[month] = {};
                    school.data[month].mm28 = item.СоблюдениеЗаПериод;
                }
            }
        }
        
        return Array.from(schoolsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [allData, filteredMonthsList]);

    // Функция загрузки детальной информации об ОУ за конкретный месяц
    const loadDetailsForMonth = useCallback(async (school, month) => {
        if (!school) return;
        
        setIsLoadingDetails(true);
        setSchoolDetails(null);
        
        try {
            const requestBody = {
                Наименование_района: school.district,
                Наименование_региона: school.region,
                Наименование_пищеблока: school.name,
                год: currentYear,
                месяц: month
            };
            
            const response = await fetch(API_SCHOOL_DETAILS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });
            
            if (!response.ok) {
                throw new Error(`Ошибка загрузки: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.error) {
                setSchoolDetails({ error: data.error });
            } else if (Array.isArray(data) && data.length === 0) {
                setSchoolDetails({ error: `Нет данных о нарушениях за ${fullMonthNames[month]} ${currentYear}` });
            } else {
                setSchoolDetails(data);
                setDetailsMonth(month);
            }
        } catch (error) {
            console.error('Ошибка загрузки деталей:', error);
            setSchoolDetails({ error: 'Не удалось загрузить данные. Проверьте соединение.' });
        } finally {
            setIsLoadingDetails(false);
        }
    }, [currentYear]);

    const openSchoolDetails = useCallback((school, month) => {
        setSelectedSchool(school);
        setDetailsMonth(month);
        setSchoolModalOpen(true);
        loadDetailsForMonth(school, month);
    }, [loadDetailsForMonth]);

    const handleMonthChange = useCallback((newMonth) => {
        if (!selectedSchool) return;
        setDetailsMonth(newMonth);
        loadDetailsForMonth(selectedSchool, newMonth);
    }, [selectedSchool, loadDetailsForMonth]);

    const closeSchoolModal = useCallback(() => {
        setSchoolModalOpen(false);
        setSelectedSchool(null);
        setSchoolDetails(null);
        setDetailsMonth(null);
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
    }, []);

    const handleQuarterChange = (e) => {
        const quarter = e.target.value;
        const newFiltered = quarter === 'all' ? [...monthsList] : quarterMonths[quarter].filter(m => monthsList.includes(m));
        setFilteredMonthsList(newFiltered);
        setCurrentFilter(prev => ({ ...prev, quarter }));
    };

    const handleRegionChange = (e) => {
        setCurrentFilter(prev => ({ ...prev, region: e.target.value, district: '' }));
        setExpandedRegions({});
    };

    const handleDistrictChange = (e) => {
        setCurrentFilter(prev => ({ ...prev, district: e.target.value }));
        setExpandedRegions({});
    };

    const clearFilters = () => {
        setFilteredMonthsList([...monthsList]);
        setCurrentFilter({ quarter: currentFilter.quarter, region: '', district: '' });
        setExpandedRegions({});
    };

    const toggleRegion = (regionId) => {
        setExpandedRegions(prev => ({ ...prev, [regionId]: !prev[regionId] }));
    };

    const openSchoolsModal = async (regionName, districtName) => {
        const schools = await getSchoolsInDistrict(regionName, districtName);
        setCurrentSchools(schools);
        setCurrentDistrictName(districtName);
        setModalOpen(true);
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = '15px';
    };

    const closeModal = () => {
        setModalOpen(false);
        setCurrentSchools([]);
        setCurrentDistrictName('');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
    };

    const openMethodModal = () => {
        setMethodModalOpen(true);
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = '15px';
    };

    const closeMethodModal = () => {
        setMethodModalOpen(false);
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
    };

    const handleModalScroll = (e) => {
        e.stopPropagation();
    };

    const downloadQ42025Data = () => {
        const fileUrl = '/MM35_36_2025_Q4.xlsx';
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = 'MM35_36_2025_Q4.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportFilteredData = () => {
        const displayData = getDisplayData();
        
        let headers, rows;
        
        if (displayData.type === 'district') {
            headers = ['Показатель'];
            for (const month of filteredMonthsList) {
                headers.push(`${monthNames[month]} ММ27`, `${monthNames[month]} ММ28`);
            }
            const district = displayData.data;
            const row = [district.name];
            for (const month of filteredMonthsList) {
                const data = district.monthsData[month] || { mm27Percent: 0, mm28Percent: 0 };
                row.push(data.mm27Percent, data.mm28Percent);
            }
            rows = [row];
        } else {
            headers = ['Регион'];
            for (const month of filteredMonthsList) {
                headers.push(`${monthNames[month]} ММ27`, `${monthNames[month]} ММ28`);
            }
            rows = displayData.data.map(region => {
                const row = [region.name];
                for (const month of filteredMonthsList) {
                    const data = region.monthsData[month] || { mm27Percent: 0, mm28Percent: 0 };
                    row.push(data.mm27Percent, data.mm28Percent);
                }
                return row;
            });
        }
        
        const wsData = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [{ wch: 30 }, ...Array(filteredMonthsList.length * 2).fill({ wch: 12 })];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Мониторинг ММ27-28');
        let filename = `MM27_28_${currentYear}`;
        if (currentFilter.quarter !== 'all') filename += `_Q${currentFilter.quarter}`;
        if (currentFilter.region) filename += `_${currentFilter.region.replace(/ /g, '_')}`;
        if (currentFilter.district) filename += `_${currentFilter.district.replace(/ /g, '_')}`;
        filename += `.xlsx`;
        XLSX.writeFile(wb, filename);
    };

    const exportDistrictsToXLSX = (regionName) => {
        const districts = getDistrictsForRegion(regionName);
        const headers = ['Район'];
        for (const month of filteredMonthsList) {
            headers.push(`${monthNames[month]} ММ27`, `${monthNames[month]} ММ28`);
        }
        const rows = districts.map(district => {
            const row = [district.name];
            for (const month of filteredMonthsList) {
                const data = district.monthsData[month] || { mm27Percent: 0, mm28Percent: 0 };
                row.push(data.mm27Percent, data.mm28Percent);
            }
            return row;
        });
        const wsData = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [{ wch: 35 }, ...Array(filteredMonthsList.length * 2).fill({ wch: 12 })];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, `Районы ${regionName}`);
        XLSX.writeFile(wb, `MM27_28_${regionName}_${currentYear}.xlsx`);
    };

    const exportSchoolsToXLSX = () => {
        if (!currentSchools.length) {
            alert('Нет данных для экспорта');
            return;
        }
        const headers = ['ОУ'];
        for (const month of filteredMonthsList) {
            headers.push(`${monthNames[month]} ММ27`, `${monthNames[month]} ММ28`);
        }
        const rows = currentSchools.map(school => {
            const row = [school.name];
            for (const month of filteredMonthsList) {
                const data = school.data[month] || {};
                const mm27Text = data.mm27 === 1 ? 'Да' : (data.mm27 === 0 ? 'Нет' : '—');
                const mm28Text = data.mm28 === 1 ? 'Да' : (data.mm28 === 0 ? 'Нет' : '—');
                row.push(mm27Text, mm28Text);
            }
            return row;
        });
        const wsData = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [{ wch: 45 }, ...Array(filteredMonthsList.length * 2).fill({ wch: 10 })];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, `ОУ ${currentDistrictName}`);
        XLSX.writeFile(wb, `MM27_28_${currentDistrictName}_${currentYear}.xlsx`);
    };

    const getPeriodText = useMemo(() => {
        if (filteredMonthsList.length === 0) return 'Нет данных';
        if (currentFilter.quarter === 'all') {
            return `${fullMonthNames[monthsList[0]]} - ${fullMonthNames[monthsList[monthsList.length - 1]]} ${currentYear}`;
        }
        const months = quarterMonths[currentFilter.quarter];
        return `${currentYear} год, ${currentFilter.quarter} квартал (${monthNames[months[0]]}-${monthNames[months[months.length - 1]]})`;
    }, [currentFilter.quarter, monthsList, filteredMonthsList]);

    const displayData = getDisplayData();
    const allRegionsList = regionsData.map(r => r.name);
    const availableDistricts = currentFilter.region ? getDistrictsForRegion(currentFilter.region).map(d => d.name) : [];

    if (isLoading || regionsData.length === 0) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <div className="mm-spinner"></div>
                <p style={{ marginTop: '15px', fontSize: '14px', color: '#1a3a5c', fontWeight: '500' }}>{loadingStatus}</p>
                <div style={{ width: '300px', height: '8px', background: '#e0e6ed', borderRadius: '4px', margin: '15px auto', overflow: 'hidden' }}>
                    <div style={{ 
                        width: `${loadingProgress}%`, 
                        height: '100%', 
                        background: '#1a3a5c', 
                        transition: 'width 0.3s ease-out',
                        borderRadius: '4px'
                    }}></div>
                </div>
                <p style={{ fontSize: '12px', color: '#6c7a8e' }}>{loadingProgress}% завершено</p>
                <button 
                    onClick={forceRefresh}
                    style={{ marginTop: '15px', padding: '6px 12px', background: '#e67e22', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                >
                    🔄 Обновить
                </button>
            </div>
        );
    }

    return (
        <div className="mm-monitoring">
            <style>
                {`
                .mm-monitoring { font-family: 'Segoe UI', 'Roboto', serif; margin-top: 30px; margin-bottom: 30px; }
                .mm-header { background: #1a3a5c; color: white; padding: 15px 20px; border-radius: 8px 8px 0 0; }
                .mm-header h2 { margin: 0 0 5px 0; font-size: 18px; }
                .mm-header p { margin: 0; opacity: 0.85; font-size: 11px; }
                .mm-period-info { background: #e8edf2; padding: 8px 20px; font-size: 12px; font-weight: 500; color: #1a3a5c; border-bottom: 1px solid #d0d7de; }
                .mm-toolbar { padding: 12px 20px; background: #f8f9fc; border-bottom: 1px solid #e0e6ed; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; }
                .mm-filter-group { display: flex; align-items: center; gap: 6px; background: white; padding: 3px 13px; border-radius: 4px; border: 1px solid #cbd5e1; }
                .mm-filter-group label { font-size: 11px; font-weight: 600; color: #4a5b6e; }
                .mm-filter-group select { padding: 4px 8px; border: 1px solid #cbd5e1; border-radius: 3px; font-size: 11px; font-family: inherit; background: white; min-width: 100px; }
                .mm-filter-group select:disabled { background: #f5f5f5; cursor: not-allowed; }
                .mm-btn { padding: 5px 12px; background: white; border: 1px solid #cbd5e1; border-radius: 3px; cursor: pointer; font-size: 11px; font-weight: 500; transition: all 0.2s ease; }
                .mm-btn:hover { background: #e9ecef; }
                .mm-btn-excel { background: #10793f; border-color: #10793f; color: white; }
                .mm-btn-excel:hover { background: #0c5e32; }
                .mm-btn-method { background: #1a3a5c; border-color: #1a3a5c; color: white; }
                .mm-btn-method:hover { background: #0f2a44; }
                .mm-btn-archive { background: #8B4513; border-color: #8B4513; color: white; }
                .mm-btn-archive:hover { background: #6B3410; }
                .mm-btn-sm { padding: 3px 8px; font-size: 10px; }
                .mm-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-bottom: 20px; }
                .mm-stat-card { background: #f8f9fc; border: 1px solid #e0e6ed; border-radius: 6px; padding: 10px 15px; text-align: center; border-top: 2px solid #1a3a5c; }
                .mm-stat-card h3 { font-size: 11px; font-weight: 600; color: #4a5b6e; margin-bottom: 5px; }
                .mm-stat-card .value { font-size: 24px; font-weight: 600; color: #1a3a5c; }
                .mm-stat-card .sub-value { font-size: 13px; color: #6c7a8e; margin-top: 4px; }
                .mm-table-container { overflow-x: auto; }
                .mm-table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 13px; min-width: 600px; }
                .mm-table th, .mm-table td { padding: 6px 5px; text-align: center; border: 1px solid #d0d7de; }
                .mm-table th { background: #f1f5f9; font-weight: 600; color: #1e3a5f; font-size: 13px; text-transform: uppercase; }
                .mm-table th:first-child { text-align: left !important; }
                .mm-region-cell { font-weight: 600; text-align: left !important; background: #ffffff; font-size: 14px; min-width: 180px; }
                .mm-badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 13px; font-weight: 600; min-width: 38px; }
                .mm-badge-success { background: #e6f4ea; color: #137333; border: 1px solid #cce8d4; }
                .mm-badge-warning { background: #fff4e5; color: #ad6200; border: 1px solid #ffe6b3; }
                .mm-badge-danger { background: #fef3f2; color: #bc1c2e; border: 1px solid #fce4e2; }
                .mm-district-display { background: #e8f4fd; border-left: 3px solid #1a3a5c; }
                .mm-back-link { cursor: pointer; color: #1a3a5c; text-decoration: underline; font-size: 13px; margin-left: 10px; }
                .mm-back-link:hover { color: #e67e22; }
                
                .mm-expand-btn { 
                    background: linear-gradient(135deg, #f0f4f9 0%, #e2e8f0 100%);
                    border: 1px solid #cbd5e1;
                    color: #1a3a5c;
                    cursor: pointer;
                    font-size: 12px;
                    font-weight: bold;
                    padding: 2px 6px;
                    margin-right: 8px;
                    border-radius: 12px;
                    transition: all 0.2s ease;
                    width: 22px;
                    text-align: center;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .mm-expand-btn:hover { 
                    background: linear-gradient(135deg, #e2e8f0 0%, #d0d9e8 100%);
                    transform: scale(1.02);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    border-color: #1a3a5c;
                }
                .mm-expand-btn:active {
                    transform: scale(0.98);
                }
                
                .mm-no-districts { font-size: 9px; color: #999; margin-left: 8px; }
                .mm-detail-row { background: #fafcff; display: none; }
                .mm-detail-row.show { display: table-row; }
                .mm-detail-cell { padding: 12px 15px; background: #fafcff; }
                .mm-districts-container { width: 100%; }
                .mm-districts-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 8px; }
                .mm-districts-header h4 { margin: 0; color: #1a3a5c; font-size: 13px; }
                .mm-sub-table { width: 100%; margin-top: 8px; font-size: 13px; border-collapse: collapse; }
                .mm-sub-table th, .mm-sub-table td { padding: 4px; border: 1px solid #d0d7de; text-align: center; }
                .mm-sub-table th { background: #eef2f6; font-size: 9px; }
                .mm-sub-table th:first-child { text-align: left !important; }
                .mm-district-row { cursor: pointer; transition: background 0.2s ease; }
                .mm-district-row:hover { background: #eef2ff; }
                
                .mm-school-badge-cell {
                    cursor: pointer;
                    transition: opacity 0.2s ease;
                }
                .mm-school-badge-cell:hover {
                    opacity: 0.7;
                }
                
                .mm-school-detail-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 13px;
                }
                .mm-school-detail-table th,
                .mm-school-detail-table td {
                    padding: 8px 6px;
                    border: 1px solid #d0d7de;
                    text-align: left;
                    vertical-align: top;
                }
                .mm-school-detail-table th {
                    background: #f1f5f9;
                    font-weight: 600;
                    color: #1e3a5f;
                    width: 180px;
                }
                .mm-error-list {
                    margin: 0;
                    padding-left: 20px;
                }
                .mm-error-list li {
                    margin: 4px 0;
                }
                .mm-loading-details {
                    text-align: center;
                    padding: 40px;
                    color: #6c7a8e;
                }
                .mm-error-message {
                    background: #fef3f2;
                    border: 1px solid #fce4e2;
                    border-radius: 6px;
                    padding: 15px;
                    color: #bc1c2e;
                    text-align: center;
                }
                
                .mm-modal { 
                    display: none; 
                    position: fixed; 
                    z-index: 10000; 
                    left: 0; 
                    top: 0; 
                    width: 100%; 
                    height: 100%; 
                    background-color: rgba(0,0,0,0.5); 
                    backdrop-filter: blur(2px);
                    overflow: hidden;
                }
                .mm-modal.show { 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                }
                .mm-modal-content { 
                    background-color: white; 
                    width: 90%; 
                    max-width: 1200px; 
                    border-radius: 8px; 
                    box-shadow: 0 5px 20px rgba(0,0,0,0.2); 
                    animation: mmModalFadeIn 0.2s ease; 
                    display: flex; 
                    flex-direction: column; 
                    max-height: 85vh;
                    overflow: hidden;
                }
                .mm-modal-content.method { 
                    max-width: 700px; 
                }
                .mm-modal-content.school-detail { 
                    max-width: 1000px; 
                }
                @keyframes mmModalFadeIn { 
                    from { opacity: 0; transform: translateY(-20px); } 
                    to { opacity: 1; transform: translateY(0); } 
                }
                .mm-modal-header { 
                    background: #1a3a5c; 
                    color: white; 
                    padding: 12px 20px; 
                    border-radius: 8px 8px 0 0; 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center; 
                    flex-shrink: 0; 
                }
                .mm-modal-header h2 { 
                    font-size: 16px; 
                    font-weight: 500; 
                    margin: 0; 
                }
                .mm-modal-body { 
                    padding: 15px; 
                    overflow-y: auto; 
                    flex: 1;
                    overscroll-behavior: contain;
                }
                .mm-method-body { 
                    padding: 20px; 
                    line-height: 1.5; 
                    font-size: 13px; 
                    color: #1a2a3a; 
                    overflow-y: auto; 
                    flex: 1;
                    overscroll-behavior: contain;
                }
                .mm-method-body h3 { 
                    color: #1a3a5c; 
                    margin-top: 20px; 
                    margin-bottom: 10px; 
                    font-size: 16px; 
                    border-left: 3px solid #1a3a5c; 
                    padding-left: 12px; 
                }
                .mm-method-body h4 { 
                    color: #2c5a7a; 
                    margin-top: 15px; 
                    margin-bottom: 8px; 
                    font-size: 14px; 
                }
                .mm-method-body p { 
                    margin: 3px 0; 
                }
                .mm-method-body ul, .mm-method-body ol { 
                    margin: 3px 0 8px 20px; 
                }
                .mm-method-body li { 
                    margin: 4px 0; 
                }
                .mm-method-body .note { 
                    background: #f0f4f9; 
                    padding: 12px; 
                    border-radius: 6px; 
                    margin: 15px 0; 
                    font-size: 12px; 
                    border-left: 3px solid #1a3a5c; 
                }
                
                .mm-spinner { 
                    display: inline-block; 
                    width: 40px; 
                    height: 40px; 
                    border: 4px solid #e0e6ed; 
                    border-top-color: #1a3a5c; 
                    border-radius: 50%; 
                    animation: mmSpin 0.6s linear infinite; 
                    margin-bottom: 10px; 
                }
                @keyframes mmSpin { 
                    to { transform: rotate(360deg); } 
                }
                .mm-loading { 
                    text-align: center; 
                    padding: 40px; 
                    font-size: 13px; 
                    color: #6c7a8e; 
                }
                
                .mm-modal-body table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 13px;
                }
                .mm-modal-body th,
                .mm-modal-body td {
                 
                    border: 1px solid #d0d7de;
                    text-align: center;
                }
                .mm-modal-body th {
                    background: #f1f5f9;
                    font-weight: 600;
                    color: #1e3a5f;
                }
                .mm-modal-body td:first-child {
                    text-align: left;
                }
                
                .mm-month-selector {
                    background: rgba(255,255,255,0.2);
                    border-radius: 4px;
                    padding: 3px 8px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .mm-month-selector label {
                    color: white;
                    font-size: 11px;
                    font-weight: 500;
                }
                .mm-month-selector select {
                    padding: 3px 6px;
                    font-size: 11px;
                    border-radius: 3px;
                    border: none;
                    background: white;
                    cursor: pointer;
                }
                
                @media (max-width: 768px) { 
                    .mm-region-cell { 
                        min-width: 150px; 
                    }
                    .mm-modal-content {
                        max-height: 90vh;
                    }
                }
                    /* Выделение строки с ОУ при наведении */
.mm-schools-table tbody tr:hover {
    background-color: #e8f0fe !important;
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.mm-schools-table tbody tr:hover td:first-child {
    font-weight: 600;
    color: #1a3a5c;
}
                `}
            </style>

            <div className="mm-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <p>| Показатель ММ27 | - Доля общеобразовательных организаций, регулярно и своевременно публикующих меню питания; не имеющих отклонений от нормативов потребления <br />| Показатель ММ28 | - Доля общеобразовательных организаций, в которых фактически соблюдается типовое меню</p>
                    <button 
                        className="mm-btn mm-btn-archive" 
                        onClick={downloadQ42025Data}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        📥 Скачать данные 4 кв 2025 (показатели 35 и 36)
                    </button>
                </div>
            </div>

            <div className="mm-period-info">
                📅 {getPeriodText}
                <button 
                    className="mm-btn mm-btn-method" 
                    onClick={openMethodModal}
                    style={{ float: 'right', marginTop: '-3px' }}
                >
                    📖 Краткая методика оценки
                </button>
            </div>

            <div className="mm-toolbar">
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <div className="mm-filter-group">
                        <label>Квартал:</label>
                        <select value={currentFilter.quarter} onChange={handleQuarterChange}>
                            <option value="all">Все месяцы</option>
                            <option value="1">1 квартал (Янв-Мар)</option>
                            <option value="2">2 квартал (Апр-Июн)</option>
                            <option value="3">3 квартал (Июл-Сен)</option>
                            <option value="4">4 квартал (Окт-Дек)</option>
                        </select>
                    </div>
                    <div className="mm-filter-group">
                        <label>Регион:</label>
                        <select value={currentFilter.region} onChange={handleRegionChange}>
                            <option value="">Все регионы</option>
                            {allRegionsList.map(region => (
                                <option key={region} value={region}>{region}</option>
                            ))}
                        </select>
                    </div>
                    <div className="mm-filter-group">
                        <label>Район:</label>
                        <select value={currentFilter.district} onChange={handleDistrictChange} disabled={!currentFilter.region}>
                            <option value="">Все районы</option>
                            {availableDistricts.map(district => (
                                <option key={district} value={district}>{district}</option>
                            ))}
                        </select>
                    </div>
                    <button className="mm-btn" onClick={clearFilters}>Сбросить фильтры</button>
                    <button className="mm-btn mm-btn-excel" onClick={exportFilteredData}>📎 Экспорт XLSX</button>
                </div>
            </div>

            <div style={{ padding: '15px 20px' }}>
                <div className="mm-stats">
                    <div className="mm-stat-card">
                        <h3>ММ27{currentFilter.district ? ` (${currentFilter.district})` : (currentFilter.region ? ` (${currentFilter.region})` : '')}</h3>
                        <div className="value">{stats.avgMM27}%</div>
                        <div className="sub-value">средний % за {stats.mm27Count} {getMonthWord(stats.mm27Count)}</div>
                    </div>
                    <div className="mm-stat-card">
                        <h3>ММ28{currentFilter.district ? ` (${currentFilter.district})` : (currentFilter.region ? ` (${currentFilter.region})` : '')}</h3>
                        <div className="value">{stats.avgMM28}%</div>
                        <div className="sub-value">средний % за {stats.mm28Count} {getMonthWord(stats.mm28Count)}</div>
                    </div>
                </div>

                {displayData.type === 'district' ? (
                    <div className="mm-table-container">
                        <div style={{ marginBottom: '10px', padding: '8px 12px', background: '#e8f4fd', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <strong>📌 Район: {displayData.data.name}</strong>
                                <span style={{ marginLeft: '13px', fontSize: '11px', color: '#6c7a8e' }}>(Регион: {displayData.regionName})</span>
                            </div>
                            <button 
                                className="mm-back-link" 
                                onClick={() => setCurrentFilter(prev => ({ ...prev, district: '' }))}
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            >
                                ← Вернуться к регионам
                            </button>
                        </div>
                        <table className="mm-table">
                            <thead>
                                <tr>
                                    <th style={{ minWidth: '200px', textAlign: 'left' }}>Показатель</th>
                                    {filteredMonthsList.map(m => <th key={m} colSpan="2">{monthNames[m]}</th>)}
                                </tr>
                                <tr style={{ background: '#eef2f6' }}>
                                    <th style={{ textAlign: 'left' }}></th>
                                    {filteredMonthsList.map(m => (
                                        <React.Fragment key={m}>
                                            <th>27</th>
                                            <th>28</th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="mm-district-display">
                                    <td style={{ textAlign: 'left', fontWeight: 'bold' }}>{displayData.data.name}</td>
                                    {filteredMonthsList.map(m => {
                                        const data = displayData.data.monthsData[m] || { mm27Percent: 0, mm28Percent: 0 };
                                        const mm27Class = data.mm27Percent >= 80 ? 'mm-badge-success' : (data.mm27Percent >= 50 ? 'mm-badge-warning' : 'mm-badge-danger');
                                        const mm28Class = data.mm28Percent >= 80 ? 'mm-badge-success' : (data.mm28Percent >= 50 ? 'mm-badge-warning' : 'mm-badge-danger');
                                        return (
                                            <React.Fragment key={m}>
                                                <td><span className={`mm-badge ${mm27Class}`}>{data.mm27Percent}%</span></td>
                                                <td><span className={`mm-badge ${mm28Class}`}>{data.mm28Percent}%</span></td>
                                            </React.Fragment>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                        <p style={{ marginTop: '10px', fontSize: '11px', color: '#6c7a8e', textAlign: 'center' }}>
                            💡 Нажмите на кнопку "Вернуться к регионам" чтобы снять фильтр по району
                        </p>
                    </div>
                ) : displayData.data.length === 0 ? (
                    <div className="mm-loading">Нет данных по выбранным фильтрам</div>
                ) : (
                    <>
                        <div className="mm-table-container">
                            <table className="mm-table">
                                <thead>
                                    <tr>
                                        <th style={{ minWidth: '160px', textAlign: 'left' }}>Регион</th>
                                        {filteredMonthsList.map(m => <th key={m} colSpan="2">{monthNames[m]}</th>)}
                                    </tr>
                                    <tr style={{ background: '#eef2f6' }}>
                                        <th style={{ textAlign: 'left' }}></th>
                                        {filteredMonthsList.map(m => (
                                            <React.Fragment key={m}>
                                                <th>27</th>
                                                <th>28</th>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayData.data.map(region => {
                                        const regionId = region.name.replace(/[^a-zA-Zа-яА-Я0-9]/g, '_');
                                        const districts = getDistrictsForRegion(region.name);
                                        return (
                                            <React.Fragment key={region.name}>
                                                <tr>
                                                    <td className="mm-region-cell">
                                                        {districts.length > 0 && (
                                                            <button 
                                                                className="mm-expand-btn" 
                                                                onClick={() => toggleRegion(regionId)}
                                                                title={expandedRegions[regionId] ? "Свернуть список районов" : "Развернуть список районов"}
                                                            >
                                                                {expandedRegions[regionId] ? '▲' : '▼'}
                                                            </button>
                                                        )}
                                                        <strong>{region.name}</strong>
                                                        {districts.length === 0 && <span className="mm-no-districts">(нет районов)</span>}
                                                    </td>
                                                    {filteredMonthsList.map(m => {
                                                        const data = region.monthsData[m] || { mm27Percent: 0, mm28Percent: 0 };
                                                        const mm27Class = data.mm27Percent >= 80 ? 'mm-badge-success' : (data.mm27Percent >= 50 ? 'mm-badge-warning' : 'mm-badge-danger');
                                                        const mm28Class = data.mm28Percent >= 80 ? 'mm-badge-success' : (data.mm28Percent >= 50 ? 'mm-badge-warning' : 'mm-badge-danger');
                                                        return (
                                                            <React.Fragment key={m}>
                                                                <td><span className={`mm-badge ${mm27Class}`}>{data.mm27Percent}%</span></td>
                                                                <td><span className={`mm-badge ${mm28Class}`}>{data.mm28Percent}%</span></td>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tr>
                                                {expandedRegions[regionId] && districts.length > 0 && (
                                                    <tr className="mm-detail-row show">
                                                        <td colSpan={filteredMonthsList.length * 2 + 1} className="mm-detail-cell">
                                                            <div className="mm-districts-container">
                                                                <div className="mm-districts-header">
                                                                    <h4>📋 Районы региона: {region.name}</h4>
                                                                    <button className="mm-btn mm-btn-excel mm-btn-sm" onClick={() => exportDistrictsToXLSX(region.name)}>
                                                                        📎 Экспорт районов (XLSX)
                                                                    </button>
                                                                </div>
                                                                <table className="mm-sub-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ minWidth: '160px', textAlign: 'left' }}>Район</th>
                                                                            {filteredMonthsList.map(m => <th key={m} colSpan="2">{monthNames[m]}</th>)}
                                                                        </tr>
                                                                        <tr style={{ background: '#eef2f6' }}>
                                                                            <th style={{ textAlign: 'left' }}></th>
                                                                            {filteredMonthsList.map(m => (
                                                                                <React.Fragment key={m}>
                                                                                    <th>27</th>
                                                                                    <th>28</th>
                                                                                </React.Fragment>
                                                                            ))}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {districts.map(district => (
                                                                            <tr key={district.name} className="mm-district-row" onClick={() => openSchoolsModal(region.name, district.name)}>
                                                                                <td style={{ textAlign: 'left' }}>📌 {district.name}</td>
                                                                                {filteredMonthsList.map(m => {
                                                                                    const data = district.monthsData[m] || { mm27Percent: 0, mm28Percent: 0 };
                                                                                    const mm27Class = data.mm27Percent >= 80 ? 'mm-badge-success' : (data.mm27Percent >= 50 ? 'mm-badge-warning' : 'mm-badge-danger');
                                                                                    const mm28Class = data.mm28Percent >= 80 ? 'mm-badge-success' : (data.mm28Percent >= 50 ? 'mm-badge-warning' : 'mm-badge-danger');
                                                                                    return (
                                                                                        <React.Fragment key={m}>
                                                                                            <td><span className={`mm-badge ${mm27Class}`}>{data.mm27Percent}%</span></td>
                                                                                            <td><span className={`mm-badge ${mm28Class}`}>{data.mm28Percent}%</span></td>
                                                                                        </React.Fragment>
                                                                                    );
                                                                                })}
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                    <tfoot>
                                                                        <tr style={{ background: '#f5f5f5' }}>
                                                                            <td colSpan={filteredMonthsList.length * 2 + 1} style={{ fontSize: '10px', color: '#6c7a8e', textAlign: 'center' }}>
                                                                                💡 Нажмите на район, чтобы посмотреть список школ
                                                                            </td>
                                                                        </tr>
                                                                    </tfoot>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <p style={{ marginTop: '10px', fontSize: '10px', color: '#6c7a8e' }}>
                            Нажмите на кнопку <span style={{ display: 'inline-block', background: 'linear-gradient(135deg, #f0f4f9 0%, #e2e8f0 100%)', padding: '1px 6px', borderRadius: '12px', fontSize: '9px', fontWeight: 'bold' }}>▼</span> сбоку от региона для просмотра районов. При нажатии на район откроется список ОУ.
                        </p>
                    </>
                )}
            </div>

            {/* Модальное окно со списком ОУ района */}
            <div className={`mm-modal ${modalOpen ? 'show' : ''}`} onClick={closeModal}>
                <div className="mm-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="mm-modal-header">
                        <h2>Образовательные учреждения - {currentDistrictName}</h2>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button className="mm-btn mm-btn-excel" onClick={exportSchoolsToXLSX}>📎 Экспорт XLSX</button>
                            <button onClick={closeModal} style={{ background: 'none', border: 'none', color: 'white', fontSize: '22px', cursor: 'pointer' }}>×</button>
                        </div>
                    </div>
                    <div className="mm-modal-body" ref={modalBodyRef} onWheel={handleModalScroll}>
    {currentSchools.length === 0 ? (
        <div className="mm-loading">Нет данных об образовательных учреждениях</div>
    ) : (
        <table className="mm-schools-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
                <tr>
                    <th style={{ minWidth: '250px' }}>ОУ</th>
                    {filteredMonthsList.map(m => <th key={m} colSpan="2">{monthNames[m]}</th>)}
                </tr>
                <tr style={{ background: '#eef2f6' }}>
                    <th></th>
                    {filteredMonthsList.map(m => (
                        <React.Fragment key={m}>
                            <th>27</th>
                            <th>28</th>
                        </React.Fragment>
                    ))}
                </tr>
            </thead>
            <tbody>
                {currentSchools.map((school, idx) => (
                    <tr key={idx}>
                        <td style={{ textAlign: 'left' }}>{school.name}</td>
                        {filteredMonthsList.map(m => {
                            const data = school.data[m] || {};
                            const mm27Class = data.mm27 === 1 ? 'mm-badge-successf' : (data.mm27 === 0 ? 'mm-badge-danger' : 'mm-badge-warning');
                            const mm28Class = data.mm28 === 1 ? 'mm-badge-successf' : (data.mm28 === 0 ? 'mm-badge-danger' : 'mm-badge-warning');
                            const mm27Text = data.mm27 === 1 ? '✓' : (data.mm27 === 0 ? '⚠️' : '—');
                            const mm28Text = data.mm28 === 1 ? '✓' : (data.mm28 === 0 ? '⚠️' : '—');
                            return (
                                <React.Fragment key={m}>
                                    <td 
                                        className="mm-school-badge-cell"
                                        onClick={() => openSchoolDetails(school, m)}
                                        title={`Показать нарушения за ${monthNames[m]} ${currentYear}`}
                                    >
                                        <span className={`mm-badge ${mm27Class}`}>{mm27Text}</span>
                                    </td>
                                    <td 
                                        className="mm-school-badge-cell"
                                        onClick={() => openSchoolDetails(school, m)}
                                        title={`Показать нарушения за ${monthNames[m]} ${currentYear}`}
                                    >
                                        <span className={`mm-badge ${mm28Class}`}>{mm28Text}</span>
                                    </td>
                                </React.Fragment>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    )}
</div>
                </div>
            </div>

            {/* Модальное окно с детальной информацией об ОУ */}
<div className={`mm-modal ${schoolModalOpen ? 'show' : ''}`} onClick={closeSchoolModal}>
    <div className="mm-modal-content school-detail" onClick={(e) => e.stopPropagation()}>
        <div className="mm-modal-header">
            📋 Детальная информация: {selectedSchool?.name || 'ОУ'}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div className="mm-month-selector">
                    <label>Месяц:</label>
                    <select 
                        value={detailsMonth || (filteredMonthsList.length > 0 ? filteredMonthsList[filteredMonthsList.length - 1] : 1)} 
                        onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                    >
                        {filteredMonthsList.map(m => (
                            <option key={m} value={m}>{fullMonthNames[m]}</option>
                        ))}
                    </select>
                </div>
                <button onClick={closeSchoolModal} style={{ background: 'none', border: 'none', color: 'white', fontSize: '22px', cursor: 'pointer' }}>×</button>
            </div>
        </div>
        <div className="mm-modal-body" ref={modalBodyRef} onWheel={handleModalScroll}>
            {isLoadingDetails ? (
                <div className="mm-loading-details">
                    <div className="mm-spinner" style={{ width: '30px', height: '30px' }}></div>
                    <p>Загрузка данных о нарушениях за {detailsMonth ? fullMonthNames[detailsMonth] : '...'}...</p>
                </div>
            ) : schoolDetails?.error ? (
                <div className="mm-error-message">
                    ⚠️ {schoolDetails.error}
                </div>
            ) : Array.isArray(schoolDetails) && schoolDetails.length > 0 ? (
                <div>
                    <p style={{ marginBottom: '15px', fontSize: '12px', color: '#1a3a5c' }}>
                        <strong>📍 Регион:</strong> {selectedSchool?.region} &nbsp;|&nbsp;
                        <strong>🏘️ Район:</strong> {selectedSchool?.district} &nbsp;|&nbsp;
                        <strong>📅 Месяц:</strong> {fullMonthNames[detailsMonth]} {currentYear}
                    </p>
                    <table className="mm-school-detail-table">
                        <thead>
                            <tr style={{ background: '#f1f5f9' }}>
                                <th>Дата</th>
                                <th>Время формирования</th>
                                <th>Статус</th>
                                <th>Ошибки соблюдения ТМ</th>
                                <th>Ошибки в меню</th>
                            </tr>
                        </thead>
                        <tbody>
                            {schoolDetails.map((item, idx) => {
                                // Проверяем, пустое ли время формирования
                                const isTimeEmpty = !item.ВремяФормирования || 
                                                   item.ВремяФормирования === '' || 
                                                   item.ВремяФормирования === null;
                                
                                // Проверяем опоздание
                                const hasDelay = item.Опоздание && item.Опоздание !== '' && item.Опоздание !== null;
                                
                                return (
                                    <tr key={idx} style={isTimeEmpty ? { backgroundColor: '#fff3f0' } : {}}>
                                        <td>{item.Дата || '—'}</td>
                                        <td>
                                            {isTimeEmpty ? (
                                                <span style={{ color: '#bc1c2e', fontWeight: '500' }}>—</span>
                                            ) : (
                                                item.ВремяФормирования
                                            )}
                                        </td>
                                        <td>
                                            {isTimeEmpty ? (
                                                <span className="mm-badge-danger" style={{ display: 'inline-block', backgroundColor: '#fef3f2', color: '#bc1c2e', padding: '4px 8px' }}>
                                                    ⚠️ Меню не предоставлено
                                                </span>
                                            ) : hasDelay ? (
                                                <span className="mm-badge-danger" style={{ display: 'inline-block' }}>
                                                    ⚠️ {item.Опоздание}
                                                </span>
                                            ) : (
                                               
                                                    '✓ В срок'
                                                
                                            )}
                                        </td>
                                        <td style={{ maxWidth: '250px' }}>
                                            {isTimeEmpty ? (
                                                <span style={{ color: '#6c7a8e', fontSize: '11px' }}>—</span>
                                            ) : (
                                                item.ОшибкиСоблюденияТМ ? (
                                                    <span className="mm-badge-danger" style={{ display: 'inline-block' }}>
                                                        ⚠️ {item.ОшибкиСоблюденияТМ}
                                                    </span>
                                                ) : (
                                                    
                                                        '✓ Нет ошибок'
                                                    
                                                )
                                            )}
                                        </td>
                                        <td style={{ maxWidth: '250px' }}>
                                            {isTimeEmpty ? (
                                                <span style={{ color: '#6c7a8e', fontSize: '11px' }}>—</span>
                                            ) : (
                                                item.ОшибкиВМеню ? (
                                                    <span className="mm-badge-danger" style={{ display: 'inline-block' }}>
                                                        ⚠️ {item.ОшибкиВМеню}
                                                    </span>
                                                ) : (
                                                    
                                                       '✓ Нет ошибок'
                                                    
                                                )
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <p style={{ marginTop: '15px', fontSize: '10px', color: '#6c7a8e', textAlign: 'center' }}>
                        💡 Если время формирования отсутствует — меню не было предоставлено в этот день
                    </p>
                </div>
            ) : (
                <div className="mm-error-message">
                    ℹ️ Нет данных о нарушениях за выбранный период
                </div>
            )}
        </div>
    </div>
</div>

            {/* Модальное окно с методикой оценки */}
            <div className={`mm-modal ${methodModalOpen ? 'show' : ''}`} onClick={closeMethodModal}>
                <div className="mm-modal-content method" onClick={(e) => e.stopPropagation()}>
                    <div className="mm-modal-header">
                        <h2>📖 Краткая методика оценки деятельности пищеблоков</h2>
                        <button onClick={closeMethodModal} style={{ background: 'none', border: 'none', color: 'white', fontSize: '22px', cursor: 'pointer' }}>×</button>
                    </div>
                    <div className="mm-method-body" ref={methodModalBodyRef} onWheel={handleModalScroll}>
                        <h3>📊 Показатель №27</h3>
                        <p><strong>Своевременность и качество заполнения меню</strong></p>
                        
                        <h4>Определяемые параметры (по дням):</h4>
                        <ul>
                            <li><strong>Наличие меню</strong> — факт формирования документа «Меню фактическое»</li>
                            <li><strong>Своевременность</strong> — интервал между формированием меню и датой питания не более 9 часов</li>
                            <li><strong>Наличие ошибок</strong> — данные регистра «Проверка меню. Количество ошибок»</li>
                        </ul>
                        <p><em>Исключения: выходные дни (исключаются из расчета)</em></p>

                        <h4>Итоговые показатели за месяц:</h4>
                        <ul>
                            <li>Процент дней без ошибок = (дни с нулем ошибок / рабочие дни) × 100%</li>
                            <li>Процент своевременности = (дни со своевременным меню / рабочие дни) × 100%</li>
                        </ul>

                        <h4>Критерий соответствия (выполнение обоих условий):</h4>
                        <ul>
                            <li>Процент дней без ошибок <strong>≥ 80%</strong></li>
                            <li>Процент своевременности <strong>≥ 90%</strong></li>
                        </ul>

                        <h3>📊 Показатель №28</h3>
                        <p><strong>Соблюдение типовых меню</strong></p>

                        <h4>Определяемый параметр (по дням):</h4>
                        <ul>
                            <li>Процент соответствия типовым меню — данные регистра «Соблюдение типовых меню»</li>
                        </ul>

                        <h4>Критерий успешности дня:</h4>
                        <ul>
                            <li>Процент соответствия <strong>≥ 75%</strong></li>
                        </ul>

                        <h4>Итоговая оценка за месяц:</h4>
                        <ul>
                            <li>Пищеблок признается соблюдающим требования только при <strong>100% успешных рабочих дней</strong></li>
                            <li>Наличие хотя бы одного дня с соответствием &lt; 75% влечет признание несоблюдающим за весь период</li>
                        </ul>

                        <h3>🚫 Исключаемые из оценки пищеблоки</h3>
                        <ul>
                            <li>Помеченные на удаление</li>
                            <li>Являющиеся группами (не конечные элементы справочника)</li>
                            <li>Не обслуживающие начальные классы</li>
                            <li>Содержащие в наименовании «тест» (включая родительские группы)</li>
                            <li>Имеющие статус «Закрыт» на отчетную дату</li>
                        </ul>

                        <div className="note">
                            <strong>📌 Особенности текущего месяца:</strong><br />
                            Отчет формируется по фактически наступившие даты (до текущей календарной даты включительно).
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

function getMonthWord(count) {
    if (count === 0) return 'месяцев';
    if (count === 1) return 'месяц';
    if (count >= 2 && count <= 4) return 'месяца';
    return 'месяцев';
}

export default MonitoringMM;